// Tiny inline Markdown renderer for i18n strings.
//
// We keep translation values as flat JSON strings, but some of them include
// emphasised tokens — code snippets like `ETOH_API_KEY` or warnings like
// **never expose**. Rather than splitting each sentence into 3+ keys, we
// allow `inline code` and **bold** markers in the translated string and
// expand them to <code> / <strong> nodes at render time.
//
// Supported:
//   `code`     -> <code class="gv-code">code</code>
//   **bold**   -> <strong class="gv-strong">bold</strong>
//   {N}        -> args[N] (any React node)
//
// Anything else is rendered as plain text. No HTML injection risk — we never
// touch dangerouslySetInnerHTML.
import { Fragment } from "react";

const CODE_CLASS = "px-1.5 py-0.5 bg-[color:var(--gv-mist)] rounded text-[12px] font-mono";
const STRONG_CLASS = "text-[color:var(--gv-ink)]";

export const renderInline = (template, args = []) => {
  if (template == null) return null;
  const s = String(template);
  const out = [];
  let i = 0;
  let key = 0;
  while (i < s.length) {
    // `inline code`
    if (s[i] === "`") {
      const end = s.indexOf("`", i + 1);
      if (end > -1) {
        out.push(<code key={key++} className={CODE_CLASS}>{s.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    // **bold**
    if (s[i] === "*" && s[i + 1] === "*") {
      const end = s.indexOf("**", i + 2);
      if (end > -1) {
        out.push(<strong key={key++} className={STRONG_CLASS}>{s.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // {N} placeholder
    if (s[i] === "{") {
      const end = s.indexOf("}", i + 1);
      if (end > -1) {
        const idx = parseInt(s.slice(i + 1, end), 10);
        if (Number.isInteger(idx) && idx >= 0 && idx < args.length) {
          out.push(<Fragment key={key++}>{args[idx]}</Fragment>);
          i = end + 1;
          continue;
        }
      }
    }
    // Walk to the next special character.
    let next = i;
    while (
      next < s.length
      && s[next] !== "`"
      && !(s[next] === "*" && s[next + 1] === "*")
      && s[next] !== "{"
    ) next++;
    if (next > i) {
      out.push(<Fragment key={key++}>{s.slice(i, next)}</Fragment>);
      i = next;
    } else {
      // Lone special char that didn't close — emit as-is and advance.
      out.push(<Fragment key={key++}>{s[i]}</Fragment>);
      i++;
    }
  }
  return out;
};

export default renderInline;
